/**
 * Pieces shared by the backorder and auction modules (reference 58–70): the rows every overview
 * counts, the "related pages" chips, and the status / domain filter row of the lists.
 */

import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { Auction, Backorder, Drop } from '../../api/areas/market';
import { Field, FilterBar } from '../../components/blocks';
import { QuickLinks } from '../../components/flow';
import { findHub, hubUrl, type HubId } from '../../nav/hubs';
import { useAccess } from '../../lib/access';
import { useApi, type UseApiResult } from '../../lib/useApi';
import { formatCount } from '../../lib/format';
import { FilterSelect, ResetButton } from '../registry/shared';

/** How many rows an overview reads; a list that comes back full shows its count as "200+". */
export const OVERVIEW_LIMIT = 200;

export function counted(rows: readonly unknown[], n: number): string {
  return rows.length >= OVERVIEW_LIMIT ? `${formatCount(n)}+` : formatCount(n);
}

export function useBackorderRows(): { backorders: UseApiResult<{ items: Backorder[] }>; drops: UseApiResult<{ items: Drop[] }>; reload: () => void } {
  const backorders = useApi((signal) => api.listBackorders({ limit: OVERVIEW_LIMIT }, { signal }), []);
  const drops = useApi((signal) => api.listDrops({ limit: OVERVIEW_LIMIT }, { signal }), []);
  return {
    backorders,
    drops,
    reload: () => {
      backorders.reload();
      drops.reload();
    },
  };
}

export function useAuctionRows(): UseApiResult<{ items: Auction[] }> {
  return useApi((signal) => api.listAuctions({ limit: OVERVIEW_LIMIT }, { signal }), []);
}

type Lookup = (key: string) => string;

/** The tabs of a module hub the user may open, minus the one shown. */
function useVisibleTabs(hub: HubId, here: string): string[] {
  const access = useAccess();
  const def = findHub(hub);
  return def ? access.tabs(def).map((x) => x.id).filter((x) => x !== here) : [];
}

/** Chips to the other pages of the backorder module (reference 58's row), minus the one shown. */
export function BackorderLinks({ here }: { here: string }) {
  const { t } = useTranslation('nav');
  const tr = t as unknown as Lookup;
  const tabs = useVisibleTabs('backorder', here);
  const auctions = findHub('auctions');
  const access = useAccess();
  return (
    <QuickLinks
      className="mb-5"
      label={tr('hubs.backorder.label')}
      links={[
        ...tabs.map((x) => ({ label: tr(`hubs.backorder.tabs.${x}`), to: hubUrl('backorder', x) })),
        ...(auctions && access.tabs(auctions).length ? [{ label: tr('hubs.auctions.label'), to: hubUrl('auctions') }] : []),
      ]}
    />
  );
}

/** Chips to the other pages of the auction module (reference 64's row), minus the one shown. */
export function AuctionLinks({ here }: { here: string }) {
  const { t } = useTranslation('nav');
  const tr = t as unknown as Lookup;
  const tabs = useVisibleTabs('auctions', here);
  return <QuickLinks className="mb-5" label={tr('hubs.auctions.label')} links={tabs.map((x) => ({ label: tr(`hubs.auctions.tabs.${x}`), to: hubUrl('auctions', x) }))} />;
}

/** Status select + domain search of a list, kept in the URL (`status`, `domain`). */
export function StatusDomainFilter({
  status,
  domain,
  statuses,
  label,
  onChange,
}: {
  status: string;
  domain: string;
  statuses: { value: string; label: string }[];
  label: string;
  onChange: (entries: Record<string, string>) => void;
}) {
  const { t } = useTranslation('market');
  return (
    <FilterBar actions={<ResetButton onClick={() => onChange({ status: '', domain: '' })} disabled={!status && !domain} />}>
      <FilterSelect label={label} value={status} onChange={(v) => onChange({ status: v })} anyLabel={t('any')} className="min-w-44" options={statuses} />
      <Field label={t('backorders.domain')} className="min-w-48">
        <Input
          key={domain}
          type="search"
          className="h-8"
          defaultValue={domain}
          onKeyDown={(e) => e.key === 'Enter' && onChange({ domain: e.currentTarget.value.trim() })}
          onBlur={(e) => e.target.value.trim() !== domain && onChange({ domain: e.target.value.trim() })}
          placeholder={t('backorders.domainPlaceholder')}
        />
      </Field>
    </FilterBar>
  );
}
